<template>
  <div v-if="organisations.length > 0" ref="switcherRoot" class="relative flex min-w-0">
    <button
      class="flex min-w-0 items-center gap-2 px-3 py-1.5 text-sm text-charcoal-200 hover:text-white hover:bg-charcoal-700 rounded-lg transition-colors"
      @click="isOpen = !isOpen"
    >
      <svg class="w-4 h-4 text-charcoal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
      <span class="min-w-0 max-w-[120px] truncate">{{ currentOrgName }}</span>
      <svg class="w-3 h-3 text-charcoal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      v-if="isOpen"
      class="absolute left-0 mt-2 w-64 bg-charcoal-800 rounded-md shadow-lg ring-1 ring-charcoal-600 z-50"
    >
      <div class="px-3 py-2 border-b border-charcoal-600">
        <p class="text-xs text-charcoal-400 uppercase tracking-wide">Organisation</p>
      </div>
      <div class="py-1 max-h-60 overflow-y-auto">
        <button
          v-for="org in organisations"
          :key="org.id"
          class="w-full text-left px-4 py-2 text-sm hover:bg-charcoal-700 flex items-center justify-between"
          :class="org.id === currentOrgId ? 'text-brand-400' : 'text-charcoal-200'"
          @click="switchOrg(org)"
        >
          <span class="truncate">{{ org.name }}</span>
          <svg
            v-if="org.id === currentOrgId"
            class="w-4 h-4 text-brand-400 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { useCurrentOrganisationStore } from '@/stores/current-organisation.store'
import { extractOrgId } from '@/utils/token.utils'

interface UserOrganisation {
  id: string
  name: string
  slug: string
  auth0_org_id: string | null
  role: string
}

const authStore = useAuthStore()
const currentOrganisation = useCurrentOrganisationStore()

const switcherRoot = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const organisations = ref<UserOrganisation[]>([])
const currentOrgId = computed(() => currentOrganisation.id)

const currentOrgName = computed(() => {
  const org = organisations.value.find(o => o.id === currentOrgId.value)
  return org?.name || organisations.value[0]?.name || 'Select Org'
})

async function fetchUserOrgs() {
  try {
    const apiBase = import.meta.env.VITE_BFF_URL || 'http://localhost:3001/api'
    const tokenResult = await authStore.getAccessToken()
    if (!tokenResult.ok) return

    const res = await fetch(`${apiBase}/user/organisations`, {
      headers: { Authorization: `Bearer ${tokenResult.value}` },
    })
    if (!res.ok) return

    const data = await res.json()
    organisations.value = data.organisations || []

    // If the user has no organisations, they have no access — force logout.
    //
    // ⚠️ Except a platform_admin (#2201). Two reasons, and the second is the serious one:
    //
    //  1. Having no organisation is the CORRECT state for them — `platform_admin` is global and
    //     is granted only when `user_roles.organisation_id IS NULL` (#1169). This endpoint is
    //     membership-based (`listForUser` INNER JOINs `organisation_members`) with no platform
    //     bypass, so an empty list here says nothing about their authorisation.
    //  2. It made the un-archive unreachable by the action it exists to reverse. A platform
    //     admin whose only membership is the organisation being archived was signed out — and
    //     again on every later login, because the list stays empty while it stays archived —
    //     so recovering it needed a DBA.
    //
    // For everyone else this stays exactly as it was. While the registration switch is OFF it is
    // still ALL that enforces archiving: `organisationAccessGate` (#1809) rides that switch (#2568)
    // and is not mounted, so an archived organisation's member is signed out here — but only once
    // their list comes back empty, on this component's one fetch per page load. A member with a
    // tab already open keeps access until they reload, and one who also belongs to another active
    // organisation is never signed out. While the switch is ON the gate refuses an archived organisation's requests, this fetch
    // included, so for an org-scoped token the `!res.ok` return above exits first and the member
    // lands on /organisation-unavailable, still signed in; this logout is then only a fallback for
    // a caller the gate cannot tie to the organisation (no `org_id` claim, or an organisation with
    // no Auth0 id).
    if (organisations.value.length === 0 && !authStore.hasRole('platform_admin')) {
      await authStore.logout({ returnTo: window.location.origin })
      return
    }

    // The token is what the server acts on, and it can name a different organisation from the
    // stored one: the Auth0 picker can land someone in B while A is stored (#3006).
    const tokenOrgId = extractOrgId(tokenResult.value)
    const tokenOrg = organisations.value.find(org => org.auth0_org_id === tokenOrgId)
    if (tokenOrg) {
      currentOrganisation.set(tokenOrg.id, tokenOrg.name)
      return
    }

    // Default to the first org if none is selected — OR if the stored one is not in the list just
    // fetched (#2200). The key outlives a sign-out, so on a shared browser the stored id can be
    // the PREVIOUS user's organisation; kept, it made Settings → My Organisation a dead link.
    const storedIsMine = organisations.value.some(org => org.id === currentOrgId.value)
    if (!storedIsMine && organisations.value.length > 0) {
      const firstOrg = organisations.value[0]!
      currentOrganisation.set(firstOrg.id, firstOrg.name)
    } else if (storedIsMine) {
      // The stored id is right but its name may be missing (stored before the name was kept) or
      // stale (the organisation was renamed): take it from the list just fetched (#2455).
      const stored = organisations.value.find(org => org.id === currentOrgId.value)!
      if (currentOrganisation.name !== stored.name) {
        currentOrganisation.set(stored.id, stored.name)
      }
    }
  } catch {
    // Silently fail — org switcher is optional until orgs are enabled
  }
}

async function switchOrg(org: UserOrganisation) {
  isOpen.value = false
  if (org.id === currentOrgId.value) return

  // If org has Auth0 org ID, re-authenticate with that org for org-scoped token. The store is
  // NOT written first: a cancelled login must leave the token's organisation stored (#3006) —
  // the next fetchUserOrgs reconciles the store to whichever token the sign-in produced.
  if (org.auth0_org_id) {
    await authStore.login({
      organization: org.auth0_org_id,
      appState: { returnTo: window.location.pathname },
    })
  } else {
    // No Auth0 org — just refresh the page to pick up new org context
    currentOrganisation.set(org.id, org.name)
    window.location.reload()
  }
}

function handleClickOutside(event: Event) {
  const target = event.target as HTMLElement
  if (switcherRoot.value && !switcherRoot.value.contains(target)) {
    isOpen.value = false
  }
}

onMounted(() => {
  fetchUserOrgs()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
